import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  ImpactCo2Answer,
  Source,
  TransportationModeValues,
  ValuesMobilityPublic,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSPositionGeocodeField } from '@/ui/components/input/FSPositionGeocodeField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { useComputeTravelDistance } from '@/ui/hook/useComputeTravelDistance';
import { language, translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import { useImpactCo2AnswerSubmitDisabled } from '@/ui/pages/app/components/quiz-panel/co2/hooks/useImpactCo2AnswerSubmitDisabled';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { isNumber } from '@/ui/utils/form-validations';

const CATEGORY: Category = 'MOBILITY_PUBLIC';

export function MobilityPublicForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  isExternal = false,
  source = '',
  eventPosition,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
  source?: Source;
  eventPosition: PositionGeocode | undefined;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector = selectImpactCo2Answers<ValuesMobilityPublic>({
    sector: sector,
    category: CATEGORY,
    isExternal: isExternal,
  });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesMobilityPublic>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesMobilityPublic) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: source,
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesMobilityPublic, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: source,
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question if already completed */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`mobility-public` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    title={translate(
                      'ImpactQuestion.MOBILITY_PUBLIC.question-title',
                    )}
                    form={
                      <Form
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                        eventPosition={eventPosition}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.MOBILITY.transportationModeValues.${answer.values.transportationMode}`,
                      ),
                      `${answer.values.passengerCount} ${translate(
                        answer.values.passengerCount > 1
                          ? 'ImpactQuestion.MOBILITY.people'
                          : 'ImpactQuestion.MOBILITY.person',
                      )}`,
                      `${answer.values.travelDistance.toLocaleString(
                        language,
                      )} km`,
                      `${translate(
                        'ImpactQuestion.MOBILITY_PUBLIC.departurePoint-read',
                      )} : ${answer.values.departurePoint}`,
                    ]}
                    title={translate('ImpactQuestion.MOBILITY.question-title')}
                    index={i}
                    answer={answer}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate('ImpactQuestion.MOBILITY_PUBLIC.question-title')}
              form={
                <Form
                  onSubmit={handleSubmit}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                  eventPosition={eventPosition}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate(
                'ImpactQuestion.MOBILITY.add-button-title',
              )}
            />
          )}
        </>
      )}
    </>
  );
}

type Inputs = ValuesMobilityPublic & {
  departurePointPosition: PositionGeocode | undefined;
  returnTrip: boolean;
};

function Form({
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
  eventPosition,
}: {
  onSubmit: (values: ValuesMobilityPublic) => void;
  onDelete: () => void;
  defaultValues?: ValuesMobilityPublic;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
  eventPosition: PositionGeocode | undefined;
}) {
  let initialValues: Inputs = {
    transportationMode: 'MOTOR_CAR_VAN_TAXI_VEHICLE',
    passengerCount: 1,
    travelDistance: 0,
    departurePoint: '',
    tags: [],
    departurePointPosition: undefined,
    returnTrip: false,
  };
  if (defaultValues) {
    initialValues = {
      ...defaultValues,
      departurePointPosition: undefined,
      returnTrip: false,
    };
  }

  const { handleSubmit, register, setValue, formState, control, watch } =
    useForm<Inputs>({ defaultValues: initialValues });

  const { errors } = formState;

  const transportationMode = watch('transportationMode');
  const departurePointPosition = watch('departurePointPosition');
  const returnTrip = watch('returnTrip');

  const formatPosition = (position: PositionGeocode | undefined) => {
    if (!position) return '';
    return position.properties.address;
  };

  const onDistanceComputed = (distance: number | undefined) => {
    setValue('departurePoint', formatPosition(departurePointPosition));
    setValue('travelDistance', distance || 0);
  };

  useComputeTravelDistance({
    fromPosition: departurePointPosition,
    toPosition: eventPosition,
    returnTrip,
    transportationMode,
    onDistanceComputed,
  });

  const handleFormSubmit = (data: ValuesMobilityPublic) => {
    const values = {
      ...data,
      travelDistance: parseFloat(data.travelDistance.toString()),
      passengerCount: parseInt(data.passengerCount.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          <FSTextField
            select
            defaultValue={initialValues.transportationMode}
            label={translate(
              'ImpactQuestion.MOBILITY_PUBLIC.transportationModeTitle',
            )}
            register={{
              ...register('transportationMode'),
            }}
            error={!!errors.transportationMode}
            helperText={errors.transportationMode?.message}
          >
            {TransportationModeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.MOBILITY.transportationModeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>
          <FSTextField
            type="number"
            label={translate(
              'ImpactQuestion.MOBILITY_PUBLIC.passengerCountTitle',
            )}
            defaultValue={initialValues.passengerCount}
            register={{
              ...register('passengerCount', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.passengerCount}
            helperText={errors.passengerCount?.message}
          />
          <Controller
            name="departurePointPosition"
            control={control}
            render={({ field }) => (
              <FSPositionGeocodeField
                label={translate(
                  'ImpactQuestion.MOBILITY_PUBLIC.departurePointTitle',
                )}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            <FSTextField
              sx={{ flex: 1 }}
              type="number"
              label={translate(
                'ImpactQuestion.MOBILITY_PUBLIC.travelDistanceTitle',
              )}
              defaultValue={initialValues.travelDistance}
              register={{
                ...register('travelDistance', {
                  required: translate('Form.fill-the-field'),
                  validate: isNumber,
                }),
              }}
              error={!!errors.travelDistance}
              helperText={errors.travelDistance?.message}
              step={0.1}
            />
            <FormControlLabel
              sx={{ flex: 1 }}
              label={
                <Typography>
                  {translate('ImpactQuestion.MOBILITY_PUBLIC.returnTrip')}
                </Typography>
              }
              control={
                <Checkbox {...register('returnTrip')} checked={returnTrip} />
              }
            />
          </Stack>
          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
